export function isHeic(file) {
  return (
    /image\/hei[cf]/i.test(file.type || "") ||
    /\.(heic|heif)$/i.test(file.name || "")
  );
}

function renameToJpg(name) {
  return (name || "photo").replace(/\.(heic|heif)$/i, ".jpg");
}

// HEIC/HEIF isn't decodable by createImageBitmap in Chrome/Firefox. Try the
// native path first (Safari can do it), then fall back to a libheif worker.
export async function heicToImage(file) {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = drawToCanvas(bitmap, bitmap.width, bitmap.height);
    if (bitmap.close) bitmap.close();
    const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
    return new File([blob], renameToJpg(file.name), { type: "image/jpeg" });
  } catch {
    const { decodeHeic } = await import("./heicDecoder.js");
    const { width, height, data } = await decodeHeic(await file.arrayBuffer());
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").putImageData(new ImageData(data, width, height), 0, 0);
    const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
    return new File([blob], renameToJpg(file.name), { type: "image/jpeg" });
  }
}

export function computeTargetSize(width, height, maxEdge) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

function drawToCanvas(bitmap, w, h) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas;
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Image encoding failed"))),
      type,
      quality
    );
  });
}

export async function processImage(file) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error("Could not read this image. Try a JPEG or PNG.");
  }

  const { width, height } = bitmap;
  const fullSize = computeTargetSize(width, height, 2560);
  const gridSize = computeTargetSize(width, height, 1000);
  const lqipSize = computeTargetSize(width, height, 32);

  const full = await canvasToBlob(
    drawToCanvas(bitmap, fullSize.width, fullSize.height),
    "image/webp",
    0.82
  );
  const grid = await canvasToBlob(
    drawToCanvas(bitmap, gridSize.width, gridSize.height),
    "image/webp",
    0.8
  );
  const lqip = drawToCanvas(bitmap, lqipSize.width, lqipSize.height).toDataURL(
    "image/webp",
    0.5
  );

  if (bitmap.close) bitmap.close();
  return { full, grid, lqip, width, height };
}
